import theme, { ACCENT, getCSSVariable, getTheme } from "@/config/theme";
import type { Top5Customers } from "@/features/cost/types/weekly-cost-report.types";
import { CUSTOMER_STATUS_COLORS } from "@/features/cost/constants/weekly-cost.constants";

/**
 * Return the color configuration for customer status badges used in insight tiles.
 * Provides background, border and text colors for Review/Watch/Stable statuses.
 * Watch uses pink in light theme and warning (yellow) in dark theme.
 * @returns {{Review: {bg: string, border: string, text: string}, Watch: {...}, Stable: {...}}}
 */
export const getCustomerStatusColors = () => {
  const isLight = getTheme() === "light";
  return {
    Review: {
      bg: getCSSVariable("--bg-error-soft"),
      border: getCSSVariable("--status-error"),
      text: getCSSVariable("--accent-aqua"),
    },
    Watch: {
      bg: isLight
        ? getCSSVariable("--accent-pink")
        : getCSSVariable("--bg-warning-soft"),
      border: isLight
        ? getCSSVariable("--accent-pink")
        : getCSSVariable("--status-warning"),
      text: getCSSVariable("--accent-aqua"),
    },
    Stable: {
      bg: getCSSVariable("--bg-success-soft"),
      border: getCSSVariable("--status-success"),
      text: getCSSVariable("--accent-aqua"),
    },
  };
};

/**
 * Map a numeric change percentage into a customer status label.
 * - values > 10 => 'Review'
 * - values > 5 => 'Watch'
 * - otherwise => 'Stable'
 * @param {number} val
 * @returns {keyof typeof CUSTOMER_STATUS_COLORS}
 */
export const getCustomerStatus = (
  val: number,
): keyof typeof CUSTOMER_STATUS_COLORS => {
  if (val > 10) return "Review";
  if (val > 5) return "Watch";
  return "Stable";
};

/**
 * Convert Top5Customers array into ECharts scatter series data points.
 * Each point value contains [customer, currentCost, changePercent, previousCost, status]
 * and an itemStyle for border color.
 * @param {Top5Customers[]} customers
 */
export const buildScatterChartData = (customers: Top5Customers[]) => {
  const isLight = getTheme() === "light";
  return customers.map((c) => {
    const status = getCustomerStatus(c.changePercent);
    const statusColorMap: Record<string, string> = {
      Review: ACCENT.orange,
      Watch: isLight ? ACCENT.pink : ACCENT.yellow,
      Stable: ACCENT.green,
    };
    const color = statusColorMap[status];
    return {
      value: [
        c.customer,
        c.currentCost,
        c.changePercent,
        c.previousCost,
        status,
      ],
      itemStyle: {
        color: "transparent",
        borderColor: color,
        borderWidth: 2,
      },
    };
  });
};

/**
 * Provide themed color sets used for insight tiles (border, gradient background, solid bg, badge color, number color).
 * Returns an array of theme objects matching insight positions.
 */
export const getInsightThemes = (): Array<{
  border: string;
  bg: string;
  bgSolid: string;
  badge: string;
  num: string;
}> => [
  {
    border: ACCENT.chartBlue,
    bg: `linear-gradient(90deg, ${theme.getCSSVariable("--bg-info-soft")} 0%, ${theme.bg.canvas} 100%)`,
    bgSolid: theme.getCSSVariable("--bg-info-soft"),
    badge: ACCENT.chartBlue,
    num: theme.text.primary,
  },
  {
    border: ACCENT.purple,
    bg: `linear-gradient(90deg, ${theme.getCSSVariable("--bg-purple-soft")} 0%, ${theme.bg.canvas} 100%)`,
    bgSolid: theme.getCSSVariable("--bg-purple-soft"),
    badge: ACCENT.purple,
    num: theme.text.primary,
  },
  {
    border: ACCENT.aqua,
    bg: `linear-gradient(90deg, ${theme.getCSSVariable("--bg-aqua-soft")} 0%, ${theme.bg.canvas} 100%)`,
    bgSolid: theme.getCSSVariable("--bg-aqua-soft"),
    badge: ACCENT.aqua,
    num: theme.text.primary,
  },
  {
    border: ACCENT.orange,
    bg: `linear-gradient(90deg, ${theme.status.warning} 8%, ${theme.bg.canvas} 100%)`,
    bgSolid: theme.getCSSVariable("--bg-warning-soft"),
    badge: ACCENT.orange,
    num: theme.text.primary,
  },
  {
    border: ACCENT.green,
    bg: `linear-gradient(90deg, ${theme.status.success} 8%, ${theme.bg.canvas} 100%)`,
    bgSolid: theme.getCSSVariable("--bg-success-soft"),
    badge: ACCENT.green,
    num: theme.text.primary,
  },
];
